/**
 * The keyboard of a button + listbox dropdown, as pure functions.
 *
 * Written apart from the component that draws it for the same reason
 * `doclist-types.ts` holds the tree edits: the component needs a DOM and a
 * pointer and cannot be unit-tested, while WHICH KEY DOES WHAT is exactly the
 * part that is easy to get wrong and easy to assert.
 *
 * The pattern this implements is the ARIA one (APG "Select-Only Combobox"):
 *
 *   * A `<button aria-haspopup="listbox" aria-expanded aria-controls>` showing
 *     the current choice.
 *   * A `<ul role="listbox">` of `<li role="option">`, `aria-selected` on the
 *     chosen one.
 *   * ONE focus model, not two. This one moves DOM focus to the LIST and
 *     points `aria-activedescendant` at the active option; the options
 *     themselves are never focused and never have a tabindex. Mixing the two
 *     -- roving focus AND activedescendant -- is the usual bug, and it
 *     announces twice or not at all depending on the screen reader.
 *
 * Nothing here knows what the options are: it deals in a count and an index,
 * so the same reducer serves any list. The only thing it knows about content
 * is `typeAhead`, which takes the labels.
 */

/** What a key does. `index` is an option index, never a DOM node. */
export type ListboxAction =
  | { kind: 'none' }
  /** Open the list with this option active. */
  | { kind: 'open'; active: number }
  /** Move the active option, list staying open. */
  | { kind: 'move'; active: number }
  /** Take this option and close. */
  | { kind: 'choose'; index: number }
  /** Close, changing nothing. */
  | { kind: 'close' }

export interface ListboxKey {
  action: ListboxAction
  /**
   * Whether the component should `preventDefault()`.
   *
   * False for Tab even though Tab closes the list: swallowing it would make
   * the dropdown a keyboard trap, which is worse than anything it fixes.
   */
  prevent: boolean
}

const NOTHING: ListboxKey = { action: { kind: 'none' }, prevent: false }

function clamp(n: number, count: number): number {
  if (count <= 0) return 0
  return Math.min(Math.max(n, 0), count - 1)
}

/**
 * A key pressed on the CLOSED button.
 *
 * Enter and Space are absent on purpose: a `<button>` already turns both into
 * a click, and handling them here as well would open the list and then let the
 * click toggle it shut again -- a dropdown that flickers and stays closed.
 */
export function closedKey(key: string, selected: number, count: number): ListboxKey {
  if (count <= 0) return NOTHING
  if (key === 'ArrowDown' || key === 'ArrowUp') {
    return { action: { kind: 'open', active: clamp(selected, count) }, prevent: true }
  }
  if (key === 'Home') return { action: { kind: 'open', active: 0 }, prevent: true }
  if (key === 'End') return { action: { kind: 'open', active: count - 1 }, prevent: true }
  return NOTHING
}

/**
 * A key pressed while the list is OPEN and has focus.
 *
 * Arrows CLAMP rather than wrap. With three options a wrap is barely a
 * feature, and stopping at the end tells a person they are at the end without
 * their having to read anything.
 */
export function openKey(key: string, active: number, count: number): ListboxKey {
  if (count <= 0) return { action: { kind: 'close' }, prevent: false }
  switch (key) {
    case 'ArrowDown':
      return { action: { kind: 'move', active: clamp(active + 1, count) }, prevent: true }
    case 'ArrowUp':
      return { action: { kind: 'move', active: clamp(active - 1, count) }, prevent: true }
    case 'Home':
      return { action: { kind: 'move', active: 0 }, prevent: true }
    case 'End':
      return { action: { kind: 'move', active: count - 1 }, prevent: true }
    case 'Enter':
    case ' ':
      return { action: { kind: 'choose', index: clamp(active, count) }, prevent: true }
    case 'Escape':
      return { action: { kind: 'close' }, prevent: true }
    case 'Tab':
      // Closes and moves on. NOT prevented: see ListboxKey.prevent.
      return { action: { kind: 'close' }, prevent: false }
    default:
      return NOTHING
  }
}

/**
 * Type a letter, land on the next option that starts with it.
 *
 * Searches from the one AFTER `from` and wraps, so pressing the same letter
 * twice walks through the options that share it rather than sticking on the
 * first. Returns -1 when nothing matches, which means "do nothing" -- never
 * move to an option the letter did not name.
 *
 * One character, not a typed-ahead buffer: these lists are three roles long,
 * and a buffer needs a timer, which needs a clock, which is the sort of thing
 * that makes a pure function stop being one.
 */
export function typeAhead(labels: string[], char: string, from: number): number {
  if (char.length !== 1 || char === ' ') return -1
  const needle = char.toLowerCase()
  for (let step = 1; step <= labels.length; step++) {
    const i = (from + step + labels.length) % labels.length
    if ((labels[i] ?? '').toLowerCase().startsWith(needle)) return i
  }
  return -1
}
